import { PropertyHelper } from '@/helpers/property.helper';
import { UserHelper } from '@/helpers/user.helper';
import { AlertService } from '@/services/alert.service';
import { AppConfigService } from '@/services/app-config.service';
import { AfterViewInit, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { PropertiesService, PropertyPerson, RealProperty, RescueService } from '@eemo/api-client';
import { Subscription } from 'rxjs';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { PropertyPeopleDialogComponent } from '../property-people-dialog/property-people-dialog.component';
import { DateFormatPipe } from '../../extensions/datePipe';
import { MatMenuModule } from '@angular/material/menu';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';

import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { NgIf } from '@angular/common';
import { MatExpansionModule } from '@angular/material/expansion';

@Component({
    selector: 'app-property-people-table',
    templateUrl: './property-people-table.component.html',
    styleUrls: ['./property-people-table.component.scss'],
    standalone: true,
    imports: [MatExpansionModule, NgIf, MatButtonModule, MatIconModule, MatFormFieldModule, MatInputModule, MatSlideToggleModule, MatTableModule, MatSortModule, MatMenuModule, DateFormatPipe]
})
export class PropertyPeopleTableComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() prop: RealProperty;
  @Input() rescueMode = false;
  @ViewChild(MatSort) sort: MatSort;

  dataSource: MatTableDataSource<PropertyPerson>;
  displayFullColumns = false;
  tableClass = 'light';
  lightColumns = ['firstName', 'name', 'gender', 'age'];
  fullColumns = ['firstName', 'name', 'gender', 'age', 'type', 'birthDate', 'birthPlace'];
  rescueColumns = ['firstName', 'name', 'gender', 'age'];
  displayedColumns = [];
  panelDescription: string;
  expanded = false;
  canUpdateProperty = false;

  sub = new Subscription();
  sortSub = new Subscription();
  deleteSub = new Subscription();

  constructor(
    private propertyService: PropertiesService,
    private rescueService: RescueService,
    private propHelper: PropertyHelper,
    private dialog: MatDialog,
    private alertService: AlertService,
    private appConfigService: AppConfigService,
    private userHelper: UserHelper,
    private changeDetectorRefs: ChangeDetectorRef
  ) {
    this.displayedColumns = this.lightColumns;
  }

  ngOnInit() {
    this.canUpdateProperty = this.userHelper.canUpdateProperty(this.prop);
    if (this.canUpdateProperty) {
      this.lightColumns.push('actions');
      this.fullColumns.push('actions');
    }
    this.loadData();
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
    this.sortSub.unsubscribe();
    this.deleteSub.unsubscribe();
  }

  loadData() {
    let filter: string;
    if (this.dataSource && this.dataSource.filter) {
      filter = this.dataSource.filter;
    }
    if (this.rescueMode) {
      this.displayedColumns = this.rescueColumns;
      this.sub = this.rescueService.getRealPropertyRescuePeople(this.prop.uid).subscribe((data) => {
        this.dataSource = new MatTableDataSource(this.getDisplayedData(data));
        // *ngIf break the sort : must be set in background
        setTimeout(() => (this.dataSource.sort = this.sort));
        this.panelDescription = this.getPanelDescription();
        this.expanded = this.dataSource.data.length > 0;
      });
    } else {
      this.sub = this.propertyService.getRealPropertyPersonList(this.prop.uid).subscribe((data) => {
        this.dataSource = new MatTableDataSource(this.getDisplayedData(data));
        // *ngIf break the sort : must be set in background
        setTimeout(() => (this.dataSource.sort = this.sort));
        this.panelDescription = this.getPanelDescription();
        this.expanded = this.appConfigService.isDesktop && this.dataSource.data.length > 0;
        if (filter) {
          this.applyFilter(filter);
        }
      });
    }
  }

  getDisplayedData(data: PropertyPerson[]): any[] {
    const displayedData = [];
    data.forEach((i) => {
      const row = {
        person: i,
        firstName: i.firstName,
        name: i.name,
        birthDate: i.birthDate,
        birthPlace: i.birthPlace,
        type: this.propHelper.getPersonTypeName(i.type),
        gender: this.propHelper.getPersonGenderName(i.gender),
        age: new Date().getFullYear() - new Date(i.birthDate).getFullYear()
      };
      displayedData.push(row);
    });
    return displayedData;
  }

  ngAfterViewInit() {}

  toggleDisplayColumns() {
    if (this.displayFullColumns) {
      this.displayFullColumns = false;
      this.displayedColumns = this.lightColumns;
      this.tableClass = 'light';
    } else {
      this.displayFullColumns = true;
      this.displayedColumns = this.fullColumns;
      this.tableClass = 'full';
    }
    this.changeDetectorRefs.detectChanges();
  }

  applyFilter(filterValue: string) {
    this.dataSource.filter = filterValue.trim().toLowerCase();

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  add() {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(PropertyPeopleDialogComponent, {
        // width: '20rem'
        data: { prop: this.prop }
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.loadData();
        }
      });
    }
  }

  edit(personToEdit: PropertyPerson) {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(PropertyPeopleDialogComponent, {
        // width: '20rem'
        data: { prop: this.prop, person: personToEdit, edition: true }
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.loadData();
        }
      });
    }
  }

  confirmDelete(person: PropertyPerson) {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        // width: '25rem',
        data: {
          message: "Confirmez-vous la suppression de l'occupant ?"
        }
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.doDelete(person);
        }
      });
    }
  }

  doDelete(person: PropertyPerson) {
    this.deleteSub = this.propertyService.deleteRealPropertyPerson(this.prop.uid, person.uid).subscribe((result) => {
      this.alertService.success('Suppression effectuée avec succès');
      this.loadData();
    });
  }

  getPanelDescription() {
    const valuesCount = this.dataSource.data ? this.dataSource.data.length : 0;
    switch (valuesCount) {
      case 0:
        return 'Aucune personne';
      case 1:
        return '1 personne';
      default:
        return valuesCount + ' personnes';
    }
  }
}
